import { Calendar, Clock, MapPin, ChevronLeft, ChevronRight, Plus, Pencil, Pause, Trash2 } from "lucide-react";

const TrainingManagementPreview = () => {
  // Calendar data for January 2026
  const currentMonth = "January 2026";
  const selectedDate = 2; // January 2nd
  
  // Training days (Fridays, Saturdays, Sundays in January 2026)
  const trainingDays = [2, 3, 4, 9, 10, 11, 16, 17, 18, 23, 24, 25, 30, 31];
  
  // Calendar grid (starting from Sunday)
  const calendarDays = [
    // Row 1: Dec 28-Jan 3
    { day: 28, isPrevMonth: true }, { day: 29, isPrevMonth: true }, { day: 30, isPrevMonth: true }, { day: 31, isPrevMonth: true }, { day: 1, isPrevMonth: false }, { day: 2, isPrevMonth: false }, { day: 3, isPrevMonth: false },
    // Row 2: Jan 4-10
    { day: 4, isPrevMonth: false }, { day: 5, isPrevMonth: false }, { day: 6, isPrevMonth: false }, { day: 7, isPrevMonth: false }, { day: 8, isPrevMonth: false }, { day: 9, isPrevMonth: false }, { day: 10, isPrevMonth: false },
    // Row 3: Jan 11-17
    { day: 11, isPrevMonth: false }, { day: 12, isPrevMonth: false }, { day: 13, isPrevMonth: false }, { day: 14, isPrevMonth: false }, { day: 15, isPrevMonth: false }, { day: 16, isPrevMonth: false }, { day: 17, isPrevMonth: false },
    // Row 4: Jan 18-24
    { day: 18, isPrevMonth: false }, { day: 19, isPrevMonth: false }, { day: 20, isPrevMonth: false }, { day: 21, isPrevMonth: false }, { day: 22, isPrevMonth: false }, { day: 23, isPrevMonth: false }, { day: 24, isPrevMonth: false },
    // Row 5: Jan 25-31
    { day: 25, isPrevMonth: false }, { day: 26, isPrevMonth: false }, { day: 27, isPrevMonth: false }, { day: 28, isPrevMonth: false }, { day: 29, isPrevMonth: false }, { day: 30, isPrevMonth: false }, { day: 31, isPrevMonth: false },
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const hasTraining = (day: number, isPrevMonth: boolean) => {
    if (isPrevMonth) return false;
    return trainingDays.includes(day);
  };

  return (
    <div className="w-full h-full bg-card rounded-xl sm:rounded-2xl border border-border/50 shadow-lg overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-border/50 bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm sm:text-base text-foreground">Training Schedule</h3>
            <p className="text-xs text-muted-foreground">Manage and view training sessions</p>
          </div>
          <button className="flex items-center gap-1.5 bg-accent hover:bg-accent/90 text-accent-foreground px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Schedule Training</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-border/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-accent" />
          <span className="text-sm font-medium text-foreground">{currentMonth}</span>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-muted/50 transition-colors">
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/50 rounded transition-colors">
            Today
          </button>
          <button className="p-1.5 rounded hover:bg-muted/50 transition-colors">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 p-2 sm:p-3 overflow-hidden flex flex-col">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[10px] sm:text-xs font-medium text-muted-foreground py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 flex-1">
          {calendarDays.map((dateItem, index) => {
            const isSelected = !dateItem.isPrevMonth && dateItem.day === selectedDate;
            const hasEvent = hasTraining(dateItem.day, dateItem.isPrevMonth);
            
            return (
              <div
                key={index}
                className={`
                  relative p-1 sm:p-1.5 rounded-md flex flex-col items-center justify-start min-h-[36px] sm:min-h-[44px]
                  ${dateItem.isPrevMonth ? 'opacity-40' : ''}
                  ${isSelected ? 'ring-2 ring-accent bg-accent/10' : 'hover:bg-muted/30'}
                  transition-colors cursor-pointer
                `}
              >
                <span className={`text-[10px] sm:text-xs font-medium ${isSelected ? 'text-accent' : 'text-foreground'}`}>
                  {dateItem.day}
                </span>
                {hasEvent && (
                  <div className="mt-0.5 w-full px-0.5">
                    <div className="bg-accent text-accent-foreground text-[6px] sm:text-[8px] font-medium px-0.5 sm:px-1 py-0.5 rounded truncate text-center leading-tight">
                      TRAINING
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Session Details */}
      <div className="p-3 sm:p-4 border-t border-border/30 bg-muted/20">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-3.5 h-3.5 text-accent" />
          <span className="text-xs font-medium text-foreground">Friday, January 2, 2026</span>
        </div>
        
        <div className="bg-card rounded-lg p-2.5 sm:p-3 border border-border/30">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-xs sm:text-sm text-foreground">TRAINING</h4>
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">4:30 PM</p>
              <div className="flex items-center gap-1 mt-1.5 text-[10px] sm:text-xs text-muted-foreground">
                <MapPin className="w-3 h-3" />
                <span className="truncate">Footballhub Eco Grandeur</span>
              </div>
              <span className="inline-block mt-1.5 text-[9px] sm:text-[10px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground">
                Weekly Training
              </span>
            </div>
            
            <div className="flex flex-col gap-1">
              <button className="flex items-center gap-1 px-2 py-1 bg-muted hover:bg-muted/80 rounded text-[10px] sm:text-xs font-medium text-foreground transition-colors">
                <Pencil className="w-2.5 h-2.5" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button className="flex items-center gap-1 px-2 py-1 bg-muted hover:bg-muted/80 rounded text-[10px] sm:text-xs font-medium text-foreground transition-colors">
                <Pause className="w-2.5 h-2.5" />
                <span className="hidden sm:inline">Postpone</span>
              </button>
              <button className="flex items-center gap-1 px-2 py-1 bg-destructive/10 hover:bg-destructive/20 rounded text-[10px] sm:text-xs font-medium text-destructive transition-colors">
                <Trash2 className="w-2.5 h-2.5" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingManagementPreview;
